import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  completeRestaurantOnboarding,
  createOrResumeRestaurantOnboarding,
  getRestaurantOnboardingSession,
  updateRestaurantOnboardingSession,
} from '@/services/restaurantOnboardingApi';
import {
  COMPLETION_STATES,
  evaluateCompletionRecovery,
} from '@/lib/restaurantOnboardingCompletion';

const PHASES = {
  idle: 'idle',
  loading: 'loading',
  ready: 'ready',
  completionPending: 'completionPending',
  completionRetryable: 'completionRetryable',
  completionBlocked: 'completionBlocked',
  completed: 'completed',
  stale: 'stale',
  staleMissingRequest: 'staleMissingRequest',
  error: 'error',
};

function classifyError(error) {
  const status = error?.response?.status;
  if (status === 401) return 'authenticationExpired';
  if (status === 403) return 'permissionDenied';
  if (status === 422) return 'validationError';
  if (status === 503 || !error?.response) return 'temporaryError';
  return 'genericError';
}

function storageKeys(userId) {
  const prefix = `unigate.restaurantOnboarding.${userId}`;
  return {
    clientRequestId: `${prefix}.client_request_id`,
    sessionId: `${prefix}.session_id`,
  };
}

function phaseFromSession(session) {
  const recovery = evaluateCompletionRecovery(session);

  if (recovery.state === COMPLETION_STATES.completed) {
    return PHASES.completed;
  }

  if (recovery.state === COMPLETION_STATES.pending) {
    return PHASES.completionPending;
  }

  if (recovery.state === COMPLETION_STATES.retryable) {
    return PHASES.completionRetryable;
  }

  if (recovery.state === COMPLETION_STATES.blocked) {
    return PHASES.completionBlocked;
  }

  return PHASES.ready;
}

function applySession(setSession, setPhase, session) {
  setSession(session);
  setPhase(phaseFromSession(session));
}

export function useRestaurantOnboarding(user) {
  const [phase, setPhase] = useState(PHASES.idle);
  const [session, setSession] = useState(null);
  const [errorKey, setErrorKey] = useState(null);
  const [saveErrorKey, setSaveErrorKey] = useState(null);
  const [completionErrorKey, setCompletionErrorKey] = useState(null);
  const [conflictDraft, setConflictDraft] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completion, setCompletion] = useState(null);

  const initializationRef = useRef(null);
  const initializationUserRef = useRef(null);
  const authenticatedUserId = user?.id ?? user?._id;
  const currentAuthenticatedUserIdRef = useRef(authenticatedUserId);
  const actionInFlightUserRef = useRef(null);
  const submittedDraftRef = useRef(null);
  currentAuthenticatedUserIdRef.current = authenticatedUserId;
  const keys = useMemo(
    () => (authenticatedUserId ? storageKeys(authenticatedUserId) : null),
    [authenticatedUserId]
  );
  const enabled = user?.role === 'company_admin' && Boolean(keys);

  const createOrResume = useCallback(async () => {
    let clientRequestId = sessionStorage.getItem(keys.clientRequestId);
    if (!clientRequestId) {
      clientRequestId = crypto.randomUUID();
      sessionStorage.setItem(keys.clientRequestId, clientRequestId);
    }
    const authoritativeSession = await createOrResumeRestaurantOnboarding(clientRequestId);
    sessionStorage.setItem(keys.sessionId, authoritativeSession.id);
    return authoritativeSession;
  }, [keys]);

  const initialize = useCallback(async () => {
    const storedSessionId = sessionStorage.getItem(keys.sessionId);

    if (storedSessionId) {
      try {
        const authoritativeSession =
          await getRestaurantOnboardingSession(storedSessionId);

        if (authoritativeSession.status === 'COMPLETED') {
          const authoritativeCompletion =
            await completeRestaurantOnboarding(
              authoritativeSession.id,
              authoritativeSession.revision
            );

          return {
            session: authoritativeSession,
            completion: authoritativeCompletion,
          };
        }

        return { session: authoritativeSession };
      } catch (error) {
        if (error?.response?.status === 404) {
          sessionStorage.removeItem(keys.sessionId);

          return {
            stale: true,
            canRecover: Boolean(
              sessionStorage.getItem(keys.clientRequestId)
            ),
          };
        }

        throw error;
      }
    }

    return { session: await createOrResume() };
  }, [createOrResume, keys]);

  const applyResult = useCallback((result) => {
    setErrorKey(null);

    if (result.stale) {
      setSession(null);
      setPhase(result.canRecover ? PHASES.stale : PHASES.staleMissingRequest);
      return;
    }

    applySession(setSession, setPhase, result.session);

    if (result.completion) {
      setCompletion(result.completion);
    }
  }, []);

  const applyError = useCallback((error) => {
    setSession(null);
    setErrorKey(classifyError(error));
    setPhase(PHASES.error);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setPhase(PHASES.loading);
    setErrorKey(null);
    if (initializationUserRef.current !== authenticatedUserId) {
      initializationRef.current = null;
      initializationUserRef.current = authenticatedUserId;
    }
    if (!initializationRef.current) {
      initializationRef.current = initialize();
    }
    initializationRef.current.then(
      (result) => active && applyResult(result),
      (error) => active && applyError(error)
    );
    return () => {
      active = false;
    };
  }, [applyError, applyResult, authenticatedUserId, enabled, initialize]);

  useEffect(() => {
    setConflictDraft(null);
    setSaveErrorKey(null);
    setCompletionErrorKey(null);
    setIsCompleting(false);
    setCompletion(null);
    submittedDraftRef.current = null;
  }, [authenticatedUserId]);

  const retry = useCallback(async () => {
    const requestUserId = authenticatedUserId;
    if (
      !enabled
      || phase === PHASES.loading
      || currentAuthenticatedUserIdRef.current !== requestUserId
      || actionInFlightUserRef.current === requestUserId
    ) {
      return;
    }

    actionInFlightUserRef.current = requestUserId;
    setPhase(PHASES.loading);
    setErrorKey(null);
    try {
      const result = await initialize();
      if (currentAuthenticatedUserIdRef.current !== requestUserId) return;
      applyResult(result);
    } catch (error) {
      if (currentAuthenticatedUserIdRef.current !== requestUserId) return;
      applyError(error);
    } finally {
      if (actionInFlightUserRef.current === requestUserId) {
        actionInFlightUserRef.current = null;
      }
    }
  }, [applyError, applyResult, authenticatedUserId, enabled, initialize, phase]);

  const recover = useCallback(async () => {
    const requestUserId = authenticatedUserId;
    if (
      !enabled
      || phase === PHASES.loading
      || currentAuthenticatedUserIdRef.current !== requestUserId
      || actionInFlightUserRef.current === requestUserId
    ) {
      return;
    }

    const clientRequestId = sessionStorage.getItem(keys.clientRequestId);
    if (!clientRequestId) {
      setSession(null);
      setErrorKey(null);
      setPhase(PHASES.staleMissingRequest);
      return;
    }
    actionInFlightUserRef.current = requestUserId;
    setPhase(PHASES.loading);
    setErrorKey(null);
    try {
      const authoritativeSession = await createOrResumeRestaurantOnboarding(clientRequestId);
      if (currentAuthenticatedUserIdRef.current !== requestUserId) return;
      sessionStorage.setItem(keys.sessionId, authoritativeSession.id);
      applySession(setSession, setPhase, authoritativeSession);
    } catch (error) {
      if (currentAuthenticatedUserIdRef.current !== requestUserId) return;
      applyError(error);
    } finally {
      if (actionInFlightUserRef.current === requestUserId) {
        actionInFlightUserRef.current = null;
      }
    }
  }, [applyError, authenticatedUserId, enabled, keys, phase]);

  const saveDraft = useCallback(async (restaurant) => {
    const requestUserId = authenticatedUserId;
    const requestSession = session;
    if (
      !enabled
      || phase !== PHASES.ready
      || !requestSession?.id
      || currentAuthenticatedUserIdRef.current !== requestUserId
      || actionInFlightUserRef.current === requestUserId
    ) {
      // Surface a safe, generic message so a blocked save is never silent.
      if (currentAuthenticatedUserIdRef.current === requestUserId) {
        setSaveErrorKey('saveNotReady');
      }
      return false;
    }

    actionInFlightUserRef.current = requestUserId;
    setIsSaving(true);
    setSaveErrorKey(null);
    submittedDraftRef.current = restaurant;
    try {
      const authoritativeSession = await updateRestaurantOnboardingSession(
        requestSession.id,
        requestSession.revision,
        restaurant
      );
      if (currentAuthenticatedUserIdRef.current !== requestUserId) {
        return false;
      }

      applySession(setSession, setPhase, authoritativeSession);
      setConflictDraft(null);
      return true;
    } catch (error) {
      if (currentAuthenticatedUserIdRef.current !== requestUserId) {
        return false;
      }

      if (error?.response?.status === 409) {
        try {
          const authoritativeSession = await getRestaurantOnboardingSession(requestSession.id);

          if (currentAuthenticatedUserIdRef.current !== requestUserId) {
            return false;
          }

          applySession(setSession, setPhase, authoritativeSession);
          setConflictDraft(submittedDraftRef.current);
          setSaveErrorKey('revisionConflict');
        } catch (reloadError) {
          if (currentAuthenticatedUserIdRef.current === requestUserId) {
            setSaveErrorKey(classifyError(reloadError));
          }
        }
      } else {
        setSaveErrorKey(classifyError(error));
      }

      return false;
    } finally {
      if (actionInFlightUserRef.current === requestUserId) {
        actionInFlightUserRef.current = null;
      }

      if (currentAuthenticatedUserIdRef.current === requestUserId) {
        setIsSaving(false);
      }
    }
  }, [authenticatedUserId, enabled, phase, session]);

  const checkCompletionStatus = useCallback(async () => {
    const requestUserId = authenticatedUserId;
    const requestSession = session;

    if (
      !enabled
      || !requestSession?.id
      || currentAuthenticatedUserIdRef.current !== requestUserId
      || actionInFlightUserRef.current === requestUserId
    ) {
      return false;
    }

    actionInFlightUserRef.current = requestUserId;
    setCompletionErrorKey(null);

    try {
      const authoritativeSession =
        await getRestaurantOnboardingSession(
          requestSession.id
        );

      if (
        currentAuthenticatedUserIdRef.current !== requestUserId
      ) {
        return false;
      }

      applySession(
        setSession,
        setPhase,
        authoritativeSession
      );

      return true;
    } catch (error) {
      if (
        currentAuthenticatedUserIdRef.current !== requestUserId
      ) {
        return false;
      }

      setCompletionErrorKey(classifyError(error));

      return false;
    } finally {
      if (actionInFlightUserRef.current === requestUserId) {
        actionInFlightUserRef.current = null;
      }
    }
  }, [authenticatedUserId, enabled, session]);

  const completeOnboarding = useCallback(async () => {
    const requestUserId = authenticatedUserId;
    const requestSession = session;

    if (
      !enabled
      || !requestSession?.id
      || ![
        PHASES.ready,
        PHASES.completionRetryable,
      ].includes(phase)
      || currentAuthenticatedUserIdRef.current !== requestUserId
      || actionInFlightUserRef.current === requestUserId
    ) {
      return false;
    }

    actionInFlightUserRef.current = requestUserId;
    setIsCompleting(true);
    setCompletionErrorKey(null);

    try {
      const authoritativeCompletion = await completeRestaurantOnboarding(
        requestSession.id,
        requestSession.revision
      );

      if (
        currentAuthenticatedUserIdRef.current !== requestUserId
      ) {
        return false;
      }

      const authoritativeSession =
        await getRestaurantOnboardingSession(
          requestSession.id
        );

      if (
        currentAuthenticatedUserIdRef.current !== requestUserId
      ) {
        return false;
      }

      applySession(
        setSession,
        setPhase,
        authoritativeSession
      );
      setCompletion(authoritativeCompletion);

      return authoritativeSession.status === 'COMPLETED';
    } catch (error) {
      if (
        currentAuthenticatedUserIdRef.current !== requestUserId
      ) {
        return false;
      }

      const status = error?.response?.status;

      const ambiguousFailure =
        !error?.response
        || status === 409
        || status === 500
        || status === 503;

      if (ambiguousFailure) {
        try {
          const authoritativeSession =
            await getRestaurantOnboardingSession(
              requestSession.id
            );

          if (
            currentAuthenticatedUserIdRef.current
            !== requestUserId
          ) {
            return false;
          }

          applySession(
            setSession,
            setPhase,
            authoritativeSession
          );

          return authoritativeSession.status === 'COMPLETED';
        } catch (reloadError) {
          if (currentAuthenticatedUserIdRef.current === requestUserId) {
            setCompletionErrorKey(classifyError(reloadError));
          }

          return false;
        }
      }

      if (status === 422) {
        setCompletionErrorKey('completionNotReady');
      } else {
        setCompletionErrorKey(classifyError(error));
      }
      return false;
    } finally {
      if (actionInFlightUserRef.current === requestUserId) {
        actionInFlightUserRef.current = null;
      }
      if (currentAuthenticatedUserIdRef.current === requestUserId) {
        setIsCompleting(false);
      }
    }
  }, [authenticatedUserId, enabled, phase, session]);

  const identityChanged = initializationUserRef.current !== null
    && initializationUserRef.current !== authenticatedUserId;

  return {
    phase: identityChanged ? PHASES.loading : phase,
    session: identityChanged ? null : session,
    errorKey: identityChanged ? null : errorKey,
    saveErrorKey: identityChanged ? null : saveErrorKey,
    completionErrorKey: identityChanged ? null : completionErrorKey,
    isSaving: identityChanged ? false : isSaving,
    isCompleting: identityChanged ? false : isCompleting,
    completion: identityChanged ? null : completion,
    retry,
    recover,
    saveDraft,
    completeOnboarding,
    checkCompletionStatus,
    conflictDraft: identityChanged ? null : conflictDraft,
    dismissConflict: () => {
      setConflictDraft(null);
      setSaveErrorKey(null);
      submittedDraftRef.current = null;
    },
  };
}
